/** Which calendar the host is looking at inside one space. */
export type HostCalendarScopeKind = 'all' | 'room' | 'tutor' | 'custom' | 'none';

/** Combined space calendar, one room or tutor, or a chosen subset. */
export interface HostCalendarScope {
  kind: HostCalendarScopeKind;
  resourceId: string | null;
  /** Rooms included when `kind` is `custom`. */
  roomIds: ReadonlySet<string>;
  /** Tutors included when `kind` is `custom`. */
  tutorIds: ReadonlySet<string>;
}

const EMPTY_IDS: ReadonlySet<string> = new Set();

export function hostCalendarScopeAll(): HostCalendarScope {
  return { kind: 'all', resourceId: null, roomIds: EMPTY_IDS, tutorIds: EMPTY_IDS };
}

export function hostCalendarScopeNone(): HostCalendarScope {
  return { kind: 'none', resourceId: null, roomIds: EMPTY_IDS, tutorIds: EMPTY_IDS };
}

export function hostCalendarScopeRoom(roomId: string): HostCalendarScope {
  return { kind: 'room', resourceId: roomId, roomIds: EMPTY_IDS, tutorIds: EMPTY_IDS };
}

export function hostCalendarScopeTutor(tutorId: string): HostCalendarScope {
  return { kind: 'tutor', resourceId: tutorId, roomIds: EMPTY_IDS, tutorIds: EMPTY_IDS };
}

export function hostCalendarScopeCustom(
  roomIds: Iterable<string>,
  tutorIds: Iterable<string>,
): HostCalendarScope {
  return {
    kind: 'custom',
    resourceId: null,
    roomIds: new Set(roomIds),
    tutorIds: new Set(tutorIds),
  };
}

function sameIds(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false;
  let matches = true;
  a.forEach((id) => {
    if (!b.has(id)) matches = false;
  });
  return matches;
}

function idsKnownTo(ids: Iterable<string>, known: ReadonlySet<string>): Set<string> {
  const next = new Set<string>();
  const visit = (id: string) => {
    if (known.has(id)) next.add(id);
  };
  if (ids instanceof Set) {
    ids.forEach(visit);
    return next;
  }
  Array.from(ids).forEach(visit);
  return next;
}

function setContainsAll(container: ReadonlySet<string>, required: ReadonlySet<string>): boolean {
  let every = true;
  required.forEach((id) => {
    if (!container.has(id)) every = false;
  });
  return every;
}

function onlyId(ids: ReadonlySet<string>): string | undefined {
  let id: string | undefined;
  ids.forEach((value) => {
    id = value;
  });
  return id;
}

export function hostCalendarScopesEqual(a: HostCalendarScope, b: HostCalendarScope): boolean {
  return (
    a.kind === b.kind &&
    a.resourceId === b.resourceId &&
    sameIds(a.roomIds, b.roomIds) &&
    sameIds(a.tutorIds, b.tutorIds)
  );
}

/** Chip highlight. "All" does not highlight every room and tutor. */
export function hostCalendarScopeSelectsRoom(scope: HostCalendarScope, roomId: string): boolean {
  switch (scope.kind) {
    case 'all':
    case 'tutor':
    case 'none':
      return false;
    case 'room':
      return scope.resourceId === roomId;
    case 'custom':
      return scope.roomIds.has(roomId);
    default:
      return false;
  }
}

export function hostCalendarScopeSelectsTutor(scope: HostCalendarScope, tutorId: string): boolean {
  switch (scope.kind) {
    case 'all':
    case 'room':
    case 'none':
      return false;
    case 'tutor':
      return scope.resourceId === tutorId;
    case 'custom':
      return scope.tutorIds.has(tutorId);
    default:
      return false;
  }
}

/** Room rows belong to the combined calendar and to that room's calendar. */
export function hostCalendarScopeShowsRoomEvent(scope: HostCalendarScope, roomId: string): boolean {
  switch (scope.kind) {
    case 'all':
      return true;
    case 'room':
      return scope.resourceId === roomId;
    case 'tutor':
    case 'none':
      return false;
    case 'custom':
      return scope.roomIds.has(roomId);
    default:
      return false;
  }
}

/**
 * Tutor lessons stay on that tutor's calendar. A lesson also appears on the
 * room calendar when it is held in that room, so the room shows occupancy.
 */
export function hostCalendarScopeShowsTutorEvent(
  scope: HostCalendarScope,
  args: { tutorId?: string | null; roomId?: string | null },
): boolean {
  switch (scope.kind) {
    case 'all':
      return true;
    case 'none':
      return false;
    case 'tutor': {
      const id = args.tutorId?.trim();
      return Boolean(id) && id === scope.resourceId;
    }
    case 'room': {
      const id = args.roomId?.trim();
      return Boolean(id) && id === scope.resourceId;
    }
    case 'custom': {
      const tutor = args.tutorId?.trim();
      if (tutor && scope.tutorIds.has(tutor)) return true;
      const room = args.roomId?.trim();
      return room ? scope.roomIds.has(room) : false;
    }
    default:
      return false;
  }
}

/** A single room or tutor has nothing to switch between. */
export function hostCalendarScopeChooserVisible(args: {
  roomCount: number;
  tutorCount: number;
}): boolean {
  return args.roomCount + args.tutorCount > 1;
}

/** Checkbox state for the chooser, expanded from All / one calendar / a subset. */
export function expandHostCalendarScopeSelection(
  scope: HostCalendarScope,
  allRoomIds: Iterable<string>,
  allTutorIds: Iterable<string>,
): { roomIds: Set<string>; tutorIds: Set<string> } {
  const rooms = new Set(allRoomIds);
  const tutors = new Set(allTutorIds);
  switch (scope.kind) {
    case 'all':
      return { roomIds: rooms, tutorIds: tutors };
    case 'room': {
      const id = scope.resourceId;
      return {
        roomIds: id && rooms.has(id) ? new Set([id]) : new Set(),
        tutorIds: new Set(),
      };
    }
    case 'tutor': {
      const id = scope.resourceId;
      return {
        roomIds: new Set(),
        tutorIds: id && tutors.has(id) ? new Set([id]) : new Set(),
      };
    }
    case 'custom':
      return {
        roomIds: idsKnownTo(scope.roomIds, rooms),
        tutorIds: idsKnownTo(scope.tutorIds, tutors),
      };
    case 'none':
      return { roomIds: new Set(), tutorIds: new Set() };
    default:
      return { roomIds: new Set(), tutorIds: new Set() };
  }
}

/** Drop a selection that no longer exists after a reload or space change. */
export function resolveHostCalendarScope(args: {
  current: HostCalendarScope;
  roomIds: Iterable<string>;
  tutorIds: Iterable<string>;
}): HostCalendarScope {
  const { current, roomIds, tutorIds } = args;
  const rooms = new Set(roomIds);
  const tutors = new Set(tutorIds);
  switch (current.kind) {
    case 'all':
      return hostCalendarScopeAll();
    case 'room':
      return current.resourceId && rooms.has(current.resourceId)
        ? current
        : hostCalendarScopeAll();
    case 'tutor':
      return current.resourceId && tutors.has(current.resourceId)
        ? current
        : hostCalendarScopeAll();
    case 'custom': {
      const next = hostCalendarScopeFromSelection({
        roomIds: current.roomIds,
        tutorIds: current.tutorIds,
        allRoomIds: rooms,
        allTutorIds: tutors,
      });
      return next.kind === 'none' ? hostCalendarScopeAll() : next;
    }
    case 'none':
      return hostCalendarScopeNone();
    default:
      return hostCalendarScopeAll();
  }
}

/** Collapses a checkbox selection back to All, one calendar, a subset, or none. */
export function hostCalendarScopeFromSelection(args: {
  roomIds: Iterable<string>;
  tutorIds: Iterable<string>;
  allRoomIds: Iterable<string>;
  allTutorIds: Iterable<string>;
}): HostCalendarScope {
  const knownRooms = new Set(args.allRoomIds);
  const knownTutors = new Set(args.allTutorIds);
  const rooms = idsKnownTo(args.roomIds, knownRooms);
  const tutors = idsKnownTo(args.tutorIds, knownTutors);
  if (rooms.size === 0 && tutors.size === 0) return hostCalendarScopeNone();
  const everyRoom = setContainsAll(rooms, knownRooms);
  const everyTutor = setContainsAll(tutors, knownTutors);
  if (everyRoom && everyTutor) return hostCalendarScopeAll();
  const roomId = onlyId(rooms);
  if (rooms.size === 1 && tutors.size === 0 && roomId) {
    return hostCalendarScopeRoom(roomId);
  }
  const tutorId = onlyId(tutors);
  if (tutors.size === 1 && rooms.size === 0 && tutorId) {
    return hostCalendarScopeTutor(tutorId);
  }
  return hostCalendarScopeCustom(rooms, tutors);
}

/**
 * Inclusive start, exclusive end, using the calendar-date prefix of each ISO
 * string. FullCalendar's `startStr` / `endStr` already carry the space's wall
 * dates, including the offset.
 */
export function calendarDayKeysInRange(startIso: string, endIsoExclusive: string): string[] {
  const start = /^(\d{4}-\d{2}-\d{2})/.exec(startIso)?.[1];
  const end = /^(\d{4}-\d{2}-\d{2})/.exec(endIsoExclusive)?.[1];
  if (!start || !end) return [];
  let cursor = Date.parse(`${start}T00:00:00Z`);
  const endMs = Date.parse(`${end}T00:00:00Z`);
  if (Number.isNaN(cursor) || Number.isNaN(endMs) || cursor >= endMs) return [];
  const keys: string[] = [];
  for (let i = 0; i < 370 && cursor < endMs; i += 1) {
    keys.push(new Date(cursor).toISOString().slice(0, 10));
    cursor += 24 * 60 * 60 * 1000;
  }
  return keys;
}
